import type { PaymentRequestContext } from '@/types/payment';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

interface PaymentRequestOptions<TVariables, TData> {
  mutationFn: (variables: TVariables, context: PaymentRequestContext) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: Error, variables: TVariables) => void;
}

// 取消后忽略旧回包；接口仍使用提交时的参数快照。
const usePaymentRequest = <TVariables, TData = unknown>({
  mutationFn, onSuccess, onError,
}: PaymentRequestOptions<TVariables, TData>) => {
  const requestIdRef = useRef(0);
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const isCurrentRequest = useCallback(
    (id: number) => mountedRef.current && requestIdRef.current === id, [],
  );
  const getRequestId = useCallback(() => requestIdRef.current, []);
  const reset = useCallback(() => {
    requestIdRef.current += 1;
    pendingRef.current = false;
    if (mountedRef.current) setIsPending(false);
  }, []);

  const mutation = useMutation({
    mutationFn: ({ id, variables }: { id: number; variables: TVariables }) => mutationFn(variables, {
      isPaymentRequestCurrent: () => isCurrentRequest(id),
    }),
    onSuccess: (data, { id, variables }) => {
      if (isCurrentRequest(id)) onSuccess?.(data, variables);
    },
    onError: (error, { id, variables }) => {
      if (isCurrentRequest(id)) onError?.(error, variables);
    },
    onSettled: (_data, _error, { id }) => {
      if (!isCurrentRequest(id)) return;
      pendingRef.current = false;
      setIsPending(false);
    },
  });

  const mutate = (variables: TVariables) => {
    if (!mountedRef.current || pendingRef.current) return false;
    pendingRef.current = true;
    setIsPending(true);
    mutation.mutate({ id: ++requestIdRef.current, variables });
    return true;
  };

  return { mutate, isPending, reset, getRequestId, isCurrentRequest };
};

export default usePaymentRequest;
